import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'quill/dist/quill.snow.css';
import * as QuillNS from 'quill';

type ClipboardInput = string | { html?: string };
type ClipboardConvert = ((input: ClipboardInput) => unknown) & { __sc_patched?: boolean };

interface ClipboardConstructor {
  prototype: {
    convert: ClipboardConvert;
  };
}

interface QuillStatic {
  import?: (path: string) => ClipboardConstructor | undefined;
}

interface QuillWindow extends Window {
  Quill?: QuillStatic;
}

// PrimeReact Editor prova prima a usare la variabile globale `Quill`.
// La impostiamo qui per evitare problemi di import dinamico in alcuni setup (Vite/IIS).
// Inoltre, con Quill 2 clipboard.convert accetta solo un oggetto { html, text }:
// se qualcuno passa ancora una stringa (API Quill 1.x) la convertiamo qui.
if (typeof window !== 'undefined') {
  const w = window as QuillWindow;
  const quillModule = QuillNS as unknown as { default?: QuillStatic };
  w.Quill = w.Quill ?? quillModule.default ?? (QuillNS as unknown as QuillStatic);

  try {
    const Quill = w.Quill;
    const Clipboard = Quill?.import?.('modules/clipboard');
    const convert = Clipboard?.prototype?.convert;

    if (Clipboard?.prototype && typeof convert === 'function' && !convert.__sc_patched) {
      const original = convert;

      // Quill 2 vuole { html, text }: una stringa produrrebbe un Delta vuoto (editor vuoto).
      Clipboard.prototype.convert = function (input: ClipboardInput) {
        if (typeof input === 'string') {
          return original.call(this, { html: input, text: '' });
        }
        return original.call(this, input);
      };

      Clipboard.prototype.convert.__sc_patched = true;
    }
  } catch {
    // ignore
  }
}

import ReactDOM from 'react-dom/client';
import { createBrowserRouter, RouterProvider, Outlet } from 'react-router-dom';
import { getSettings, loadSettings } from './Helpers/SettingsHelper';
import { Paths } from './Common/Paths';
import { i18nPromise } from './Common/Translations';
import { PrimeReactProvider } from 'primereact/api';
import PublicLayout from './Layout/Public/Layout';
import PrivateLayout from './Layout/Private/Layout';
import { Error } from './Layout/Error';
import { LayoutProvider } from './Layout/Private/context/layoutcontext';
import { Login } from './Pages/Authentication/Login';
import ResetPasswordRequest from './Pages/Authentication/ResetPasswordRequest';
import Activate from './Pages/Authentication/Activate';
import Register from './Pages/Authentication/Register';
import { ToastHelper } from './Helpers/ToastHelper';
import { AuthProvider } from './Helpers/Authentication/AuthContext';
import ProtectedRoute from './Helpers/Authentication/ProtectedRoute';
import Stakeholders from './Pages/Stakeholders/Stakeholders_List';
import OperativeList from './Pages/Operative/Operative_List';
import { MailProvider } from './Pages/Mail/Context';
import AppMailLayout from './Pages/Mail/AppMailLayout';
import MailInbox from './Pages/Mail/Inbox';
import MailDetail from './Pages/Mail/Detail';
import { AxiosProvider } from './Helpers/AxiosHelper';
import axios from 'axios';
import Cookies from 'js-cookie';
import MailNew from './Pages/Mail/New';
import OperativeListNoEmails from './Pages/Operative/Operative_List_NoEmails';
import OperativeListMailToSend from './Pages/Operative/Operative_List_MailToSend';
import { SignalRProvider } from './Helpers/SignalRHelper';
import ResetPassword from './Pages/Authentication/ResetPassword';
import { DataImportNoISO } from './Pages/DataImport/DataImport_List_NoISO';
import DataImportNoBrand from './Pages/DataImport/DataImport_List_NoBrand';
import OperativeListUnreadEmails from './Pages/Operative/Operative_List_UnreadEmails';
import RolesList from './Pages/Roles/Roles_List';
import { PermissionProvider } from './Helpers/Permissions/PermissionContext';
import OperativeListToBeCalled from './Pages/Operative/Operative_List_ToBeCalled';
import DataImportList from './Pages/DataImport/DataImport_List';

const isDev = import.meta.env.DEV;

const router = createBrowserRouter([
    {
        path: '/auth',
        element: <PublicLayout><Outlet /></PublicLayout>,
        errorElement: <Error />,
        children: [
            { path: Paths['Login'].value, element: <Login /> },
            { path: Paths['ResetPasswordRequest'].value, element: <ResetPasswordRequest /> },
            { path: Paths['ResetPassword'].value, element: <ResetPassword /> },
            { path: Paths['ActivateUser'].value, element: <Activate /> },
            { path: Paths['Register'].value, element: <Register /> }
        ],
    },
    {
        path: '/',
        element:
            <PermissionProvider>
                <ProtectedRoute>
                    <PrivateLayout>
                        <Outlet />
                    </PrivateLayout>
                </ProtectedRoute>
            </PermissionProvider>,
        errorElement: !isDev ? <Error /> : undefined,
        children: [
            { path: Paths['Stakeholders'].value, element: <Stakeholders /> },
            { path: Paths['Imports'].value, element: <DataImportList /> },
            { path: Paths['ImportsNoISO'].value, element: <DataImportNoISO /> },
            { path: Paths['ImportsNoBrand'].value, element: <DataImportNoBrand /> },
            { path: Paths['Operative'].value, element: <OperativeList /> },
            { path: Paths['OperativeNoMail'].value, element: <OperativeListNoEmails /> },
            { path: Paths['OperativeListMailToSend'].value, element: <OperativeListMailToSend /> },
            { path: Paths['OperativeListUnreadEmails'].value, element: <OperativeListUnreadEmails /> },
            { path: Paths['OperativeListToBeCalled'].value, element: <OperativeListToBeCalled /> },
            { path: Paths['SettingsRoles'].value, element: <RolesList /> },
            {
                path: Paths['Mail'].value, element:
                    <MailProvider>
                        <AppMailLayout children={<MailInbox />} />
                    </MailProvider>
            },
            {
                path: Paths['MailDetail'].value, element:
                    <MailProvider>
                        <AppMailLayout children={<MailDetail />} />
                    </MailProvider>
            },
            {
                path: Paths['MailNew'].value, element:
                    <MailProvider>
                        <AppMailLayout children={<MailNew />} />
                    </MailProvider>
            }
        ],
    }
]);

const createAxiosInstance = () => {
    const settings = getSettings();
    if (!settings || !settings.settings.apiUrl) {
        console.error("Axios could not be initialized: API URL is missing from settings.");
        return null;
    }

    const AxiosHelper = axios.create({
        baseURL: settings.settings.apiUrl,
    });

    AxiosHelper.interceptors.request.use(
        (config) => {
            const token = Cookies.get('SessionToken');
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
            return config;
        },
        (error) => {
            return Promise.reject(error);
        }
    );

    return AxiosHelper;
};

const renderApp = async () => {
    try {
        await loadSettings();
        await i18nPromise;

        const AxiosInstance = createAxiosInstance(); // Now, settings are fully loaded before Axios is created
        if (!AxiosInstance) {
            console.error("Axios instance could not be created. Stopping app initialization.");
            return;
        }

        ReactDOM.createRoot(document.getElementById('root')!).render(
            <AuthProvider>
                <PrimeReactProvider>
                    <LayoutProvider>
                        <ToastHelper>
                            <AxiosProvider instance={AxiosInstance}>
                                <SignalRProvider>
                                    <RouterProvider router={router} />
                                </SignalRProvider>
                            </AxiosProvider>
                        </ToastHelper>
                    </LayoutProvider>
                </PrimeReactProvider>
            </AuthProvider>
        );
    } catch (error) {
        console.error('Failed to load configuration or i18n', error);
    }
};

renderApp();
